import React, { useState, useEffect, useCallback } from 'react';
import { Search, RotateCcw, ChevronDown, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import RoleSwitcher from './components/RoleSwitcher';
import BulkActionBar from './components/BulkActionBar';
import ApprovalGrid from './components/ApprovalGrid';
import RevertModal from './components/RevertModal';
import AuditTrailPanel from './components/AuditTrailPanel';

// ─── Real-data helpers ──────────────────────────────────────────────────────────
const formatDisplayDate = (isoDate) => {
  if (!isoDate) return '—';
  return new Date(isoDate)?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const formatDisplayDateTime = (isoTimestamp) => {
  if (!isoTimestamp) return '—';
  return new Date(isoTimestamp)?.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// Reconstructed from this single row's own timestamp/actor columns — not a true multi-event audit log
// table (none exists yet), so this only shows the row's current lifecycle stages, not a full history of
// every inline edit (inline rate/qty editing in this UI is still local-only, never persisted — pre-existing,
// unrelated to this fetch wiring).
const buildAuditHistory = (q, displayName) => {
  const steps = [];
  if (q?.submitted_at) steps.push({ type: 'submitted', actor: displayName(q?.submitted_by), timestamp: formatDisplayDateTime(q?.submitted_at) });
  if (q?.edited_at) steps.push({ type: 'edited', actor: displayName(q?.edited_by), timestamp: formatDisplayDateTime(q?.edited_at) });
  if (q?.approved_at) steps.push({ type: 'approved', actor: displayName(q?.approved_by), timestamp: formatDisplayDateTime(q?.approved_at) });
  if (q?.rejected_at) steps.push({ type: 'rejected', actor: displayName(q?.rejected_by), timestamp: formatDisplayDateTime(q?.rejected_at), note: q?.rejection_reason });
  if (q?.restored_at) steps.push({ type: 'approved', actor: displayName(q?.restored_by), timestamp: formatDisplayDateTime(q?.restored_at), note: 'Restored from a prior approved snapshot.' });
  return steps;
};

// GRADES and SEASONS stay mock — mi_mills has no grade/season columns, so there is no real reference table
// to source them from yet. State/District/Mill are now live cascading dropdowns — see millHierarchy below.
const GRADES = ['All Grades', 'M-30', 'S-30', 'L-30'];
const SEASONS = ['All Seasons', '2024-25', '2023-24'];

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Formats an ISO date (YYYY-MM-DD) as "Aug 10" for toast messages, independent of locale.
const formatShortDate = (isoDate) => {
  if (!isoDate) return '';
  const [, m, d] = isoDate?.split('-')?.map(Number);
  return `${MONTH_ABBR?.[m - 1]} ${d}`;
};

// ─── TOAST ────────────────────────────────────────────────────────────────────
const Toast = ({ message, onClose }) => (
  <div
    className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl text-sm font-semibold text-white"
    style={{ backgroundColor: '#1f2937', minWidth: 280 }}
  >
    <span className="flex-1">{message}</span>
    <button onClick={onClose} className="opacity-70 hover:opacity-100"><X size={14} /></button>
  </div>
);

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const RateApprovalQueue = () => {
  const [role, setRole] = useState('Ops Manager');
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingRows, setPendingRows] = useState([]);
  const [liveRows, setLiveRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [rowsError, setRowsError] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [filterGrade, setFilterGrade] = useState('All Grades');
  const [filterSeason, setFilterSeason] = useState('All Seasons');
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [revertTarget, setRevertTarget] = useState(null); // row pending single-row revert confirmation
  const [auditRow, setAuditRow] = useState(null);
  const [toast, setToast] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [locationDrawerOpen, setLocationDrawerOpen] = useState(false);

  // Cascading State / District / Mill filter — sourced from mi_mills, scoped to mills that have at
  // least one mill_price_quotes row (via the `!inner` embed below, which follows the real
  // mill_price_quotes.mill_id -> mi_mills.id FK). Cannot be fully tested end-to-end until
  // mill_price_quotes has real rows — currently 0 rows in the table, so this list is structurally
  // correct (verified directly against mi_mills via SQL) but will render empty until real quotes exist.
  const [selectedState, setSelectedState] = useState('All States');
  const [selectedDistrict, setSelectedDistrict] = useState('All Districts');
  const [selectedMillIds, setSelectedMillIds] = useState([]); // [] = no mill-level restriction
  const [millPopoverOpen, setMillPopoverOpen] = useState(false);
  const [millSearch, setMillSearch] = useState('');
  const [millHierarchy, setMillHierarchy] = useState([]); // [{ id, state, district, name }]
  const [hierarchyLoading, setHierarchyLoading] = useState(true);
  const [hierarchyError, setHierarchyError] = useState(null);
  const [pendingCountInScope, setPendingCountInScope] = useState(null);
  const [countLoading, setCountLoading] = useState(false);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Real fetch, replacing the old INITIAL_PENDING/INITIAL_LIVE mock arrays entirely.
  //   select q.*, m.canonical_name as mill_name
  //   from mill_price_quotes q join mi_mills m on m.id = q.mill_id
  //   order by q.submitted_at desc
  // — expressed via PostgREST embedding (mi_mills has a real FK on mill_id, confirmed via pg_constraint).
  // submitted_by/approved_by/rejected_by/restored_by are auth.users UUIDs with no FK to user_profiles,
  // so display names are resolved via a separate batch lookup. Only quota_manager/admin (the real roles
  // this page is for) can read other users' user_profiles rows per RLS — for anyone else, names of rows
  // they didn't submit/approve themselves will show as "Unknown user".
  const fetchRows = useCallback(async () => {
    setRowsLoading(true);
    setRowsError(null);
    const { data: quotes, error: quotesError } = await supabase
      ?.from('mill_price_quotes')
      ?.select('*, mi_mills(canonical_name)')
      ?.order('submitted_at', { ascending: false });

    if (quotesError) {
      setRowsError(quotesError?.message || 'Failed to load rate submissions');
      setPendingRows([]);
      setLiveRows([]);
      setRowsLoading(false);
      return;
    }

    const userIds = [...new Set(
      (quotes || [])?.flatMap(q => [q?.submitted_by, q?.approved_by, q?.rejected_by, q?.restored_by])?.filter(Boolean)
    )];
    let profilesById = {};
    if (userIds?.length > 0) {
      const { data: profiles } = await supabase
        ?.from('user_profiles')
        ?.select('user_id, full_name, email')
        ?.in('user_id', userIds);
      (profiles || [])?.forEach(p => { profilesById[p?.user_id] = p; });
    }
    const displayName = (userId) => {
      if (!userId) return '—';
      const p = profilesById?.[userId];
      return p?.full_name || p?.email || 'Unknown user';
    };

    // A live row's Revert button only makes sense if there's an earlier live snapshot for the same
    // mill/grade/season to revert to — computed from the full fetched set, no extra query needed.
    const hasPriorSnapshot = (q) => {
      if (q?.status !== 'live' || !q?.approved_at) return false;
      return (quotes || [])?.some(other =>
        other?.id !== q?.id && other?.mill_id === q?.mill_id && other?.grade === q?.grade &&
        other?.season === q?.season && other?.status === 'live' && other?.approved_at &&
        new Date(other?.approved_at) < new Date(q?.approved_at)
      );
    };

    const shaped = (quotes || [])?.map(q => ({
      id: q?.id,
      mill_id: q?.mill_id,
      millName: q?.mi_mills?.canonical_name || 'Unknown mill',
      grade: q?.grade,
      season: q?.season,
      status: q?.status === 'pending_approval' ? 'pending' : q?.status,
      rateExcl: Number(q?.rate_excl_gst) || 0,
      qty: Number(q?.available_qty) || 0,
      paymentDate: formatDisplayDate(q?.payment_date),
      liftingDate: formatDisplayDate(q?.lifting_date),
      submittedBy: displayName(q?.submitted_by),
      submittedAt: formatDisplayDateTime(q?.submitted_at),
      approvedBy: q?.approved_by ? displayName(q?.approved_by) : null,
      approvedAt: q?.approved_at ? formatDisplayDateTime(q?.approved_at) : null,
      hasPriorSnapshot: hasPriorSnapshot(q),
      _originalRateExcl: Number(q?.rate_excl_gst) || 0,
      _originalQty: Number(q?.available_qty) || 0,
      _rawStatus: q?.status,
      auditHistory: buildAuditHistory(q, displayName),
    }));

    setPendingRows(shaped?.filter(r => r?._rawStatus === 'pending_approval'));
    setLiveRows(shaped?.filter(r => r?._rawStatus === 'live'));
    setRowsLoading(false);
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setHierarchyLoading(true);
      setHierarchyError(null);
      const { data, error } = await supabase
        ?.from('mi_mills')
        ?.select('id, source_state, source_district, canonical_name, mill_price_quotes!inner(id)')
        ?.order('source_state')
        ?.order('source_district')
        ?.order('canonical_name');
      if (cancelled) return;
      if (error) {
        setHierarchyError(error?.message || 'Failed to load mill hierarchy');
        setMillHierarchy([]);
      } else {
        setMillHierarchy((data || [])?.map(m => ({
          id: m?.id, state: m?.source_state, district: m?.source_district, name: m?.canonical_name,
        })));
      }
      setHierarchyLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const stateOptions = ['All States', ...new Set(millHierarchy?.map(m => m?.state)?.filter(Boolean))];
  const districtOptions = selectedState === 'All States' ? [] : [
    'All Districts',
    ...new Set(millHierarchy?.filter(m => m?.state === selectedState)?.map(m => m?.district)?.filter(Boolean)),
  ];
  // Mills available for the checklist — State alone is enough (all mills in that state); District,
  // if picked, narrows further. District is NOT required to use the mill checklist.
  const millsForChecklist = selectedState === 'All States' ? [] :
    millHierarchy?.filter(m => m?.state === selectedState && (selectedDistrict === 'All Districts' || m?.district === selectedDistrict));

  // Real mill_ids currently in scope, given the State/District/Mill selection — used to narrow the grid.
  // Explicit mill checks override/intersect down to exactly those ids, regardless of District.
  const scopedMillIds = (() => {
    if (selectedMillIds?.length > 0) return new Set(selectedMillIds);
    if (selectedState === 'All States') return null; // no location filter active
    return new Set(
      millHierarchy
        ?.filter(m => m?.state === selectedState && (selectedDistrict === 'All Districts' || m?.district === selectedDistrict))
        ?.map(m => m?.id)
    );
  })();

  const handleStateChange = (value) => {
    setSelectedState(value);
    setSelectedDistrict('All Districts');
    setSelectedMillIds([]); // mill selections don't carry across states
    setMillSearch('');
  };
  const handleDistrictChange = (value) => {
    setSelectedDistrict(value);
    if (value !== 'All Districts') {
      // Prune only the selected mills that fall OUTSIDE the newly picked district — mills still
      // within it stay checked. Picking back to "All Districts" doesn't touch the selection at all.
      const validIds = new Set(
        millHierarchy?.filter(m => m?.state === selectedState && m?.district === value)?.map(m => m?.id)
      );
      setSelectedMillIds(prev => prev?.filter(id => validIds?.has(id)));
    }
  };
  const handleResetFilters = () => {
    setSelectedState('All States');
    setSelectedDistrict('All Districts');
    setSelectedMillIds([]);
    setMillSearch('');
  };

  // Count badge next to District — real count of pending_approval rows in the current state/district scope.
  useEffect(() => {
    let cancelled = false;
    if (selectedState === 'All States' || !scopedMillIds) {
      setPendingCountInScope(null);
      return undefined;
    }
    (async () => {
      setCountLoading(true);
      const ids = [...scopedMillIds];
      if (ids?.length === 0) {
        if (!cancelled) { setPendingCountInScope(0); setCountLoading(false); }
        return;
      }
      const { count, error } = await supabase
        ?.from('mill_price_quotes')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('status', 'pending_approval')
        ?.in('mill_id', ids);
      if (cancelled) return;
      setPendingCountInScope(error ? null : (count ?? 0));
      setCountLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState, selectedDistrict, selectedMillIds, millHierarchy]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  // Resolves the current authenticated user + their real user_profiles.role
  // (separate from the local mock RoleSwitcher, which only gates the UI).
  const getCurrentActor = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { userId: null, role: null };
    const { data: profile } = await supabase
      ?.from('user_profiles')
      ?.select('role')
      ?.eq('user_id', user.id)
      ?.single();
    return { userId: user.id, role: profile?.role || null };
  };

  // Filter logic
  const applyFilters = (rows) => {
    return rows?.filter(r => {
      const matchSearch = !search ||
        r?.millName?.toLowerCase()?.includes(search?.toLowerCase()) ||
        (r?.submittedBy || '')?.toLowerCase()?.includes(search?.toLowerCase()) ||
        (r?.approvedBy || '')?.toLowerCase()?.includes(search?.toLowerCase());
      // No location filter active (scopedMillIds === null) -> everything matches. Otherwise a row only
      // matches if its real mill_id is in scope — rows now always carry a real mill_id from the fetch.
      const matchLocation = !scopedMillIds || (r?.mill_id != null && scopedMillIds?.has(r?.mill_id));
      const matchGrade = filterGrade === 'All Grades' || r?.grade === filterGrade;
      const matchSeason = filterSeason === 'All Seasons' || r?.season === filterSeason;
      return matchSearch && matchLocation && matchGrade && matchSeason;
    });
  };

  // Role-based row filtering
  const getVisibleRows = (rows) => {
    if (role === 'Staff') {
      return rows?.filter(r => r?.submittedBy === 'Rahul Sharma'); // mock staff user
    }
    return rows;
  };

  const visiblePending = applyFilters(getVisibleRows(pendingRows));
  const visibleLive = applyFilters(getVisibleRows(liveRows));

  // BUG FIX: selectedIds used to survive filter changes, so "Approve/Reject Selected" could act on rows
  // no longer visible under the current filter. Prune (not clear) the selection down to whatever is
  // still in visiblePending whenever any filter — or the visible set itself — changes.
  useEffect(() => {
    setSelectedIds(prev => {
      const visibleIdSet = new Set(visiblePending?.map(r => r?.id));
      const next = prev?.filter(id => visibleIdSet?.has(id));
      return next?.length === prev?.length ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterGrade, filterSeason, selectedState, selectedDistrict, selectedMillIds, visiblePending]);

  const handleSelect = (id) => {
    setSelectedIds(prev => prev?.includes(id) ? prev?.filter(x => x !== id) : [...prev, id]);
  };

  // Raw writers — real concurrent-update handling: 0 rows affected means someone else already actioned
  // this row first (a genuine race against a real row, not the old fake pre-baked demo row). Split out
  // from the single-row handlers below so bulk actions can loop these without triggering a refetch per
  // row, then refetch once at the end.
  const approveOne = async (id) => {
    const { userId, role: realRole } = await getCurrentActor();
    if (!userId) return { error: 'Not signed in — cannot approve' };
    const { data, error } = await supabase
      ?.from('mill_price_quotes')
      ?.update({
        status: 'live',
        approved_by: userId,
        approved_by_role: realRole,
        approved_at: new Date()?.toISOString(),
      })
      ?.eq('id', id)
      ?.eq('status', 'pending_approval')
      ?.select('id');
    if (error) return { error: error?.message };
    if (!data || data?.length === 0) return { error: 'race' };
    return { error: null };
  };

  const rejectOne = async (id, reason) => {
    const { userId } = await getCurrentActor();
    if (!userId) return { error: 'Not signed in — cannot reject' };
    const { data, error } = await supabase
      ?.from('mill_price_quotes')
      ?.update({
        status: 'rejected',
        rejected_by: userId,
        rejected_at: new Date()?.toISOString(),
        rejection_reason: reason,
      })
      ?.eq('id', id)
      ?.eq('status', 'pending_approval')
      ?.select('id');
    if (error) return { error: error?.message };
    if (!data || data?.length === 0) return { error: 'race' };
    return { error: null };
  };

  const handleApprove = async (id) => {
    const { error } = await approveOne(id);
    if (error === 'race') {
      showToast('⚠️ Already actioned by someone else');
      await fetchRows();
      return;
    }
    if (error) {
      showToast(`❌ Approve failed: ${error}`);
      return;
    }
    setSelectedIds(prev => prev?.filter(x => x !== id));
    showToast('✅ Rate approved successfully');
    await fetchRows();
  };

  const handleReject = async (id, reason) => {
    const { error } = await rejectOne(id, reason);
    if (error === 'race') {
      showToast('⚠️ Already actioned by someone else');
      await fetchRows();
      return;
    }
    if (error) {
      showToast(`❌ Reject failed: ${error}`);
      return;
    }
    setSelectedIds(prev => prev?.filter(x => x !== id));
    showToast('❌ Rate rejected');
    await fetchRows();
  };

  // Withdraw (soft-delete) a live offer from the admin/quota_manager side — same lifecycle
  // state officers' own Withdraw button uses (status -> 'withdrawn'), so it stays visible in
  // Submission History (the audit trail) and Mandi/WhatsApp stay in sync via the same DB
  // trigger. Uses mpq_approver_update (already grants admin/quota_manager unrestricted UPDATE
  // on any row) — no new RLS policy needed, unlike the officer-side Withdraw button.
  const deleteOne = async (id) => {
    const { userId } = await getCurrentActor();
    if (!userId) return { error: 'Not signed in — cannot delete' };
    const { data, error } = await supabase
      ?.from('mill_price_quotes')
      ?.update({
        status: 'withdrawn',
        withdrawn_by: userId,
        withdrawn_at: new Date()?.toISOString(),
      })
      ?.eq('id', id)
      ?.eq('status', 'live')
      ?.select('id');
    if (error) return { error: error?.message };
    if (!data || data?.length === 0) return { error: 'race' };
    return { error: null };
  };

  const handleDelete = async (row) => {
    const confirmed = window.confirm(
      `Delete this offer for ${row?.millName} (${row?.grade})? This only removes the offer — the mill itself stays in the system.`
    );
    if (!confirmed) return;
    const { error } = await deleteOne(row?.id);
    if (error === 'race') {
      showToast('⚠️ Already actioned by someone else');
      await fetchRows();
      return;
    }
    if (error) {
      showToast(`❌ Delete failed: ${error}`);
      return;
    }
    showToast('🗑️ Offer deleted');
    await fetchRows();
  };

  // Inline rate/qty editing stays local-only (never persisted to mill_price_quotes) — pre-existing,
  // unrelated to this fetch wiring.
  const handleEdit = (id, field, value) => {
    setPendingRows(prev => prev?.map(r => r?.id === id ? { ...r, [field]: value } : r));
  };

  const handleBulkApprove = async () => {
    const ids = [...(selectedIds || [])];
    let succeeded = 0, raced = 0, failed = 0;
    for (const id of ids) {
      // eslint-disable-next-line no-await-in-loop
      const { error } = await approveOne(id);
      if (!error) succeeded += 1;
      else if (error === 'race') raced += 1;
      else failed += 1;
    }
    setSelectedIds([]);
    await fetchRows();
    const parts = [`✅ Approved ${succeeded}`];
    if (raced) parts?.push(`${raced} already actioned`);
    if (failed) parts?.push(`${failed} failed`);
    showToast(parts?.join(', '));
  };

  const handleBulkReject = async (reason) => {
    const ids = [...(selectedIds || [])];
    let succeeded = 0, raced = 0, failed = 0;
    for (const id of ids) {
      // eslint-disable-next-line no-await-in-loop
      const { error } = await rejectOne(id, reason);
      if (!error) succeeded += 1;
      else if (error === 'race') raced += 1;
      else failed += 1;
    }
    setSelectedIds([]);
    await fetchRows();
    const parts = [`❌ Rejected ${succeeded}`];
    if (raced) parts?.push(`${raced} already actioned`);
    if (failed) parts?.push(`${failed} failed`);
    showToast(parts?.join(', '));
  };

  // Cannot be fully tested until mill_price_quotes has real historical 'live' rows — currently 0 rows in the table.
  //
  // Looks up the most recent row that was actually live (approved) on or before targetDate for this
  // mill/grade/season, then INSERTs a fresh copy of THAT historical row's values as the new live row —
  // status='live', restored_from_id=<the historical row's id>, restored_by=auth.uid(), restored_at=now().
  const revertRow = async (row, userId, targetDate) => {
    if (!row?.mill_id) {
      // Mock display rows carry no real mill_id (mill_price_quotes is currently empty, so there is
      // nothing real for these mock rows to point at yet) — surface that plainly instead of a doomed insert.
      return { error: { message: `no mill_id on row ${row?.id} (mock data, not a real mill_price_quotes row)` } };
    }

    const { data: historicalRow, error: lookupError } = await supabase
      ?.from('mill_price_quotes')
      ?.select('*')
      ?.eq('mill_id', row.mill_id)
      ?.eq('grade', row.grade)
      ?.eq('season', row.season)
      ?.eq('status', 'live')
      ?.lte('approved_at', `${targetDate}T23:59:59`)
      ?.order('approved_at', { ascending: false })
      ?.limit(1)
      ?.maybeSingle();

    if (lookupError) return { error: lookupError };
    if (!historicalRow) return { error: { message: `No approved rate found for this mill/grade on or before ${targetDate}` } };

    return supabase
      ?.from('mill_price_quotes')
      ?.insert({
        mill_id: historicalRow?.mill_id,
        season: historicalRow?.season,
        grade: historicalRow?.grade,
        rate_excl_gst: historicalRow?.rate_excl_gst,
        payment_date: historicalRow?.payment_date,
        lifting_date: historicalRow?.lifting_date,
        available_qty: historicalRow?.available_qty,
        status: 'live',
        restored_from_id: historicalRow?.id,
        restored_by: userId,
        restored_at: new Date()?.toISOString(),
      })
      ?.select('id');
  };

  const handleRevert = async (row, targetDate) => {
    const { userId } = await getCurrentActor();
    if (!userId) {
      showToast('❌ Not signed in — cannot revert');
      return;
    }
    const { error } = await revertRow(row, userId, targetDate);
    if (error) {
      showToast(`❌ Revert failed: ${error?.message}`);
      return;
    }
    showToast(`↺ Rate reverted to ${formatShortDate(targetDate)}'s approved snapshot`);
    await fetchRows();
  };

  const handleRevertAll = async (targetDate) => {
    setShowRevertModal(false);
    const { userId } = await getCurrentActor();
    if (!userId) {
      showToast('❌ Not signed in — cannot revert');
      return;
    }
    let succeeded = 0;
    const failures = [];
    for (const row of visibleLive) {
      // eslint-disable-next-line no-await-in-loop
      const { error } = await revertRow(row, userId, targetDate);
      if (error) failures?.push(`${row?.millName}: ${error?.message}`);
      else succeeded += 1;
    }
    if (failures?.length > 0) {
      showToast(`↺ Reverted ${succeeded}/${visibleLive?.length} to ${formatShortDate(targetDate)}'s rates — ${failures?.length} failed (see console)`);
      // eslint-disable-next-line no-console
      console.error('Revert All failures:', failures);
    } else {
      showToast(`↺ Reverted ${succeeded} mills to ${formatShortDate(targetDate)}'s approved rates`);
    }
    await fetchRows();
  };

  const canAct = role === 'Ops Manager' || role === 'Admin';
  // "Revert All" only makes sense if at least one live row actually has an earlier snapshot to revert
  // to — computed once from the current liveRows list (not visibleLive, since the button acts on
  // whatever's in scope, but its enabled/disabled state reflects real data, not the current filter).
  const anyLiveHasPriorSnapshot = liveRows?.some(r => r?.hasPriorSnapshot === true);

  // Searchable multi-select checklist for Mill — replaces the old single-select dropdown. State alone
  // is enough to populate the list; District (if picked) narrows it further, but isn't required.
  const MillMultiSelect = () => {
    const filtered = millsForChecklist?.filter(m => m?.name?.toLowerCase()?.includes(millSearch?.toLowerCase()));
    const allFilteredSelected = filtered?.length > 0 && filtered?.every(m => selectedMillIds?.includes(m?.id));

    const toggleSelectAll = () => {
      if (allFilteredSelected) {
        setSelectedMillIds(prev => prev?.filter(id => !filtered?.some(m => m?.id === id)));
      } else {
        setSelectedMillIds(prev => [...new Set([...prev, ...(filtered?.map(m => m?.id) || [])])]);
      }
    };
    const toggleMill = (id) => {
      setSelectedMillIds(prev => prev?.includes(id) ? prev?.filter(x => x !== id) : [...prev, id]);
    };

    return (
      <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
        <label className="text-xs font-semibold" style={{ color: '#6b7280' }}>Mill</label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setMillPopoverOpen(o => !o)}
            disabled={selectedState === 'All States'}
            className="w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs font-medium outline-none disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ borderColor: '#e8ddd0', backgroundColor: '#fff', color: '#1f2937' }}
          >
            <span className="truncate">
              {selectedMillIds?.length === 0 ? 'All Mills' : `${selectedMillIds?.length} mill${selectedMillIds?.length !== 1 ? 's' : ''} selected`}
            </span>
            <ChevronDown size={12} style={{ color: '#9ca3af' }} />
          </button>

          {millPopoverOpen && selectedState !== 'All States' && (
            <div className="absolute z-30 mt-1 w-full sm:w-72 rounded-lg border bg-white shadow-lg" style={{ borderColor: '#e8ddd0' }}>
              <div className="p-2 border-b" style={{ borderColor: '#e8ddd0' }}>
                <input
                  autoFocus
                  value={millSearch}
                  onChange={e => setMillSearch(e?.target?.value)}
                  placeholder="Search mills..."
                  className="w-full rounded-md border px-2 py-1.5 text-xs outline-none"
                  style={{ borderColor: '#e8ddd0' }}
                />
              </div>
              <label
                className="flex items-center gap-2 px-3 py-2 border-b text-xs font-semibold cursor-pointer"
                style={{ borderColor: '#e8ddd0' }}
              >
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={toggleSelectAll}
                  style={{ accentColor: '#f59e0b' }}
                />
                Select all{filtered?.length > 0 ? ` (${filtered?.length})` : ''}
              </label>
              <div className="max-h-48 overflow-y-auto">
                {filtered?.length === 0 ? (
                  <p className="px-3 py-3 text-xs" style={{ color: '#9ca3af' }}>No mills match.</p>
                ) : filtered?.map(m => (
                  <label
                    key={m?.id}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-amber-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedMillIds?.includes(m?.id)}
                      onChange={() => toggleMill(m?.id)}
                      style={{ accentColor: '#f59e0b' }}
                    />
                    <span className="truncate">{m?.name}</span>
                  </label>
                ))}
              </div>
              <div className="flex items-center justify-between px-3 py-2 border-t" style={{ borderColor: '#e8ddd0' }}>
                <span className="text-[10px]" style={{ color: '#6b7280' }}>
                  {selectedMillIds?.length} of {millsForChecklist?.length} selected
                </span>
                <button
                  onClick={() => setMillPopoverOpen(false)}
                  className="px-3 py-1 rounded-md text-xs font-semibold text-white"
                  style={{ backgroundColor: '#d97706' }}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Shared State/District/Mill fields — rendered once inline on desktop, once inside the mobile drawer,
  // mirroring FactoryRateIntelligenceCenter's FilterBar FilterContent pattern.
  const LocationFilterFields = () => (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
      <div className="flex flex-col gap-1 flex-1 min-w-[140px]">
        <label className="text-xs font-semibold" style={{ color: '#6b7280' }}>State</label>
        <select
          value={selectedState}
          onChange={e => handleStateChange(e?.target?.value)}
          disabled={hierarchyLoading}
          className="appearance-none rounded-lg border px-3 py-2 text-xs font-medium outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ borderColor: '#e8ddd0', backgroundColor: '#fff', color: '#1f2937' }}
        >
          {hierarchyLoading ? <option>Loading…</option> : stateOptions?.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>
      <div className="flex flex-col gap-1 flex-1 min-w-[160px]">
        <div className="flex items-center gap-1.5">
          <label className="text-xs font-semibold" style={{ color: '#6b7280' }}>District</label>
          {selectedState !== 'All States' && (
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap"
              style={{ backgroundColor: '#fef3c7', color: '#92400e' }}
            >
              {countLoading
                ? '…'
                : selectedMillIds?.length > 0
                  ? `${pendingCountInScope ?? 0} pending across ${selectedMillIds?.length} selected mill${selectedMillIds?.length !== 1 ? 's' : ''}`
                  : `${pendingCountInScope ?? 0} pending in ${selectedDistrict !== 'All Districts' ? selectedDistrict : selectedState}`}
            </span>
          )}
        </div>
        <select
          value={selectedDistrict}
          onChange={e => handleDistrictChange(e?.target?.value)}
          disabled={selectedState === 'All States'}
          className="appearance-none rounded-lg border px-3 py-2 text-xs font-medium outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ borderColor: '#e8ddd0', backgroundColor: '#fff', color: '#1f2937' }}
        >
          {selectedState === 'All States'
            ? <option>All Districts</option>
            : districtOptions?.map(d => <option key={d}>{d}</option>)}
        </select>
      </div>
      <MillMultiSelect />
      {selectedState !== 'All States' && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold opacity-0 select-none" aria-hidden="true">Reset</label>
          <button
            onClick={handleResetFilters}
            className="text-xs font-semibold underline px-1 py-2 text-left"
            style={{ color: '#d97706' }}
          >
            Reset Filters
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8f1e9', fontFamily: 'DM Sans, Inter, sans-serif' }}>
      <RoleBasedHeader />
      {/* Top Bar */}
      <div className="sticky top-0 z-40 bg-white border-b shadow-sm" style={{ borderColor: '#e8ddd0' }}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h1 className="text-lg font-bold" style={{ color: '#1f2937' }}>Rate Approval Queue</h1>
            <RoleSwitcher role={role} onChange={r => { setRole(r); setSelectedIds([]); }} />
          </div>
          {/* Search + Filters */}
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#9ca3af' }} />
              <input
                value={search}
                onChange={e => setSearch(e?.target?.value)}
                placeholder="Search mill or staff..."
                className="w-full pl-8 pr-3 py-2 rounded-lg border text-sm outline-none"
                style={{ borderColor: '#e8ddd0', backgroundColor: '#f8f1e9', color: '#1f2937' }}
              />
            </div>

            {/* State/District/Mill — desktop: inline; mobile: single button + bottom drawer */}
            <div className="hidden sm:flex sm:flex-wrap sm:items-end sm:gap-2">
              <LocationFilterFields />
            </div>
            <div className="sm:hidden">
              <button
                onClick={() => setLocationDrawerOpen(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold"
                style={{ borderColor: '#e8ddd0', backgroundColor: '#fff', color: '#1f2937' }}
              >
                Location
                {selectedState !== 'All States' && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#f59e0b' }} />}
                <ChevronDown size={12} style={{ color: '#9ca3af' }} />
              </button>
            </div>

            {[
              { value: filterGrade, setter: setFilterGrade, options: GRADES },
              { value: filterSeason, setter: setFilterSeason, options: SEASONS },
            ]?.map((f, i) => (
              <div key={i} className="relative">
                <select
                  value={f?.value}
                  onChange={e => f?.setter(e?.target?.value)}
                  className="appearance-none pl-3 pr-7 py-2 rounded-lg border text-xs font-medium outline-none cursor-pointer"
                  style={{ borderColor: '#e8ddd0', backgroundColor: '#fff', color: '#1f2937' }}
                >
                  {f?.options?.map(o => <option key={o}>{o}</option>)}
                </select>
                <ChevronDown size={12} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2" style={{ color: '#9ca3af' }} />
              </div>
            ))}
            {hierarchyError && (
              <span className="text-xs font-medium" style={{ color: '#DC143C' }} title={hierarchyError}>
                Mill hierarchy unavailable — location filter disabled
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Location filter drawer */}
      {locationDrawerOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end sm:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setLocationDrawerOpen(false)} />
          <div className="relative bg-white rounded-t-3xl p-6 pb-8 z-10" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold" style={{ color: '#1f2937' }}>Location Filters</h3>
              <button onClick={() => setLocationDrawerOpen(false)} className="p-2 rounded-lg hover:bg-gray-100">
                <X size={18} style={{ color: '#6b7280' }} />
              </button>
            </div>
            <LocationFilterFields />
            <button
              onClick={() => setLocationDrawerOpen(false)}
              className="mt-5 w-full py-3 rounded-xl font-bold text-white text-sm"
              style={{ background: 'linear-gradient(135deg, #f59e0b, #ff8c00)' }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
      {/* Tabs */}
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 pt-4">
        <div className="flex gap-1 overflow-x-auto pb-1">
          {[
            { key: 'pending', label: 'Pending Approval', count: visiblePending?.length },
            { key: 'live', label: 'Live Rates', count: visibleLive?.length },
          ]?.map(tab => (
            <button
              key={tab?.key}
              onClick={() => { setActiveTab(tab?.key); setSelectedIds([]); }}
              className="flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-semibold whitespace-nowrap transition border-b-2"
              style={{
                backgroundColor: activeTab === tab?.key ? '#fff' : 'transparent',
                borderBottomColor: activeTab === tab?.key ? '#f59e0b' : 'transparent',
                color: activeTab === tab?.key ? '#f59e0b' : '#6b7280',
              }}
            >
              {tab?.label}
              <span
                className="px-1.5 py-0.5 rounded-full text-xs font-bold"
                style={{
                  backgroundColor: activeTab === tab?.key ? '#fef3c7' : '#f3f4f6',
                  color: activeTab === tab?.key ? '#d97706' : '#6b7280',
                }}
              >
                {tab?.count}
              </span>
            </button>
          ))}
        </div>
      </div>
      {/* Content */}
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 pb-10">
        <div className="bg-white rounded-b-xl rounded-tr-xl border p-4" style={{ borderColor: '#e8ddd0', boxShadow: '0 2px 4px rgba(0,0,0,0.06)' }}>

          {rowsLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-8 h-8 rounded-full border-2 animate-spin mb-3" style={{ borderColor: '#fde68a', borderTopColor: '#f59e0b' }} />
              <p className="text-sm" style={{ color: '#6b7280' }}>Loading rate submissions…</p>
            </div>
          ) : rowsError ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="font-semibold text-sm" style={{ color: '#DC143C' }}>Couldn't load rate submissions</p>
              <p className="text-xs mt-1" style={{ color: '#6b7280' }}>{rowsError}</p>
              <button
                onClick={fetchRows}
                className="mt-4 px-4 py-2 rounded-lg text-xs font-semibold border transition hover:bg-amber-50"
                style={{ borderColor: '#f59e0b', color: '#d97706' }}
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* Pending Tab */}
              {activeTab === 'pending' && (
                <>
                  {canAct && selectedIds?.length >= 2 && (
                    <BulkActionBar
                      selectedCount={selectedIds?.length}
                      onApproveAll={handleBulkApprove}
                      onRejectAll={handleBulkReject}
                      onClear={() => setSelectedIds([])}
                    />
                  )}
                  <ApprovalGrid
                    rows={visiblePending}
                    role={role}
                    selectedIds={selectedIds}
                    onSelect={handleSelect}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onEdit={handleEdit}
                    onRowClick={setAuditRow}
                    tab="pending"
                    isMobile={isMobile}
                  />
                </>
              )}

              {/* Live Rates Tab */}
              {activeTab === 'live' && (
                <>
                  {canAct && (
                    <div className="flex justify-end mb-3">
                      <button
                        onClick={() => setShowRevertModal(true)}
                        disabled={!anyLiveHasPriorSnapshot}
                        title={anyLiveHasPriorSnapshot ? undefined : 'No prior approved snapshot exists for any live rate yet.'}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        style={{ borderColor: '#f59e0b', color: '#d97706', backgroundColor: '#fffbeb' }}
                      >
                        <RotateCcw size={14} /> ↺ Revert All to a Past Date
                      </button>
                    </div>
                  )}
                  <ApprovalGrid
                    rows={visibleLive}
                    role={role}
                    selectedIds={[]}
                    onSelect={() => {}}
                    onApprove={(rowOrId) => setRevertTarget(typeof rowOrId === 'object' ? rowOrId : { id: rowOrId })}
                    onReject={() => {}}
                    onEdit={() => {}}
                    onDelete={handleDelete}
                    onRowClick={setAuditRow}
                    tab="live"
                    isMobile={isMobile}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
      {/* Modals & Panels */}
      {showRevertModal && (
        <RevertModal
          millCount={visibleLive?.length}
          onConfirm={(targetDate) => handleRevertAll(targetDate)}
          onCancel={() => setShowRevertModal(false)}
        />
      )}
      {revertTarget && (
        <RevertModal
          millLabel={`${revertTarget?.millName || 'this mill'} (${revertTarget?.grade || ''})`}
          onConfirm={(targetDate) => { setRevertTarget(null); handleRevert(revertTarget, targetDate); }}
          onCancel={() => setRevertTarget(null)}
        />
      )}
      {auditRow && (
        <AuditTrailPanel row={auditRow} onClose={() => setAuditRow(null)} />
      )}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
};

export default RateApprovalQueue;
